import { Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { ItemSelector, Text, Tooltip } from 'vet-bones/components';
import { isNotEqual } from 'vet-bones/utils';
import { SelectItem } from 'vet-bones/utils/select';

import {
  TEST_IDS,
  getProjectMappingSelectItemTestId,
} from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  Mapping,
  MappingIdentifierType,
} from 'src/containers/ProjectDiagram/types/data';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import {
  isMapping,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { setMappingLabel } from 'src/containers/ProjectDiagram/utils/mapping/setMappingLabel';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { useSuggestionContext } from 'src/containers/ProjectMapping/hooks/suggestion';
import {
  useMappingFromRoute,
  usePrevMappingFromRoute,
} from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { projectNodeDataSelector } from 'src/selectors/projectNodeData';
import { projectResourceFieldItemsSelector } from 'src/selectors/projectResource';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectClassMappingLabelRow: React.VFC = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const mapping = useMappingFromRoute() as Mapping & { to: string };
  const prevMapping = usePrevMappingFromRoute();
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));
  const { isLoading } = useSuggestionContext();
  // projectNodeDataSelector is a factory with a non-serializable arg, so it
  // can't be memoized at the source; memoize the instance here instead.
  const resourceDataSelector = React.useMemo(
    () => projectNodeDataSelector(projectId, mapping.from, isResource),
    [projectId, mapping.from]
  );
  const resourceData = useReduxSelector(resourceDataSelector);
  const fieldItems = useReduxSelector(
    projectResourceFieldItemsSelector(
      projectId,
      getResourceValue(mapping.from),
      resourceData?.type
    )
  ).map((field) => ({
    text: field.text,
    type: MappingIdentifierType.FIELD,
    value: field.value,
  }));

  const selectedLabel =
    fieldItems.find(({ value }) => mapping.label === value) || null;

  const handleSelectLabel = (item: SelectItem | null) => {
    if (!diagram) {
      return;
    }

    commitDiagramModel(diagram, (model) => {
      const mappingLink = model.linkDataArray.find(
        (linkData) => isMapping(linkData) && linkData.key === mapping.key
      );
      if (!mappingLink) {
        return;
      }

      setMappingLabel({
        key: mapping.key,
        label: item?.value || null,
      })(model);
    });
  };

  const highlighted =
    isDraftDiagram &&
    (prevMapping
      ? isNotEqual(mapping.label, prevMapping.label)
      : Boolean(mapping.label));

  return (
    <>
      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--border-right',
          'sd-draft-highlight-candidate',
          { 'sd-draft-highlight-candidate--highlighted': highlighted }
        )}
      >
        <div className="sd-project-mapping-grid-cell-left">
          <Icon icon={IconNames.ANNOTATION} />
          <Text>{copy.components.projectMapping.annotations.label}</Text>
          <Tooltip
            content={copy.components.projectMapping.annotations.labelHelp}
          >
            <Icon icon={IconNames.INFO_SIGN} />
          </Tooltip>
        </div>
      </div>

      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--wide',
          'sd-draft-highlight-candidate',
          { 'sd-draft-highlight-candidate--highlighted': highlighted }
        )}
        data-testid={TEST_IDS.projectMapping.annotations.selectorCell}
      >
        <ItemSelector.Single
          buttonText={selectedLabel?.text || mapping.label || ''}
          clearable
          clearTestId={TEST_IDS.selector.clear}
          disabled={isLoading}
          filterText={copy.components.defaults.filter}
          items={fieldItems}
          itemTestId={getProjectMappingSelectItemTestId}
          onSelect={handleSelectLabel}
          selected={selectedLabel || null}
          testId={TEST_IDS.projectMapping.annotations.selector}
        />
      </div>
    </>
  );
};
